import { Grade } from '@pepper/docs/src/components/SEOPaneV2/components/ContentGrade/ContentGrade.types'
import { getProgressColor } from '@pepper/docs/src/components/SEOPaneV2/components/ContentOptimize/ContentOptimize.utils'
import { getReadabilityStatement } from '@pepper/utils/src/function/get_readability'

import type { ResearchReportDocument } from '@pepper/utils/src/types/seo'

export function getMetrics(report: ResearchReportDocument) {
  return [
    {
      title: 'Readability Grade',
      value: getReadabilityStatement(report.readability_ease.min).level,
    },
    {
      title: 'Content Length',
      value: renderMetricAsString(
        report.page_length.min,
        report.page_length.max
      ),
    },
    {
      title: 'Headings',
      value: renderMetricAsString(
        report.element_counts.hx.min,
        report.element_counts.hx.max
      ),
    },
    {
      title: 'Links',
      value: renderMetricAsString(
        report.element_counts.a.min,
        report.element_counts.a.max
      ),
      info: 'Recommended number of links you should be using in the content',
    },
  ]
}

function getColor(value: number, min: number, max: number) {
  const metricValue = Math.floor(value)
  const metricMin = Math.floor(min)
  const metricMax = Math.floor(max)

  const percentage =
    metricMax === 0
      ? 0
      : Math.ceil((metricValue / ((metricMin + metricMax) / 2)) * 100)

  const color = getProgressColor(percentage, {
    min: metricMin,
    max: metricMax,
    value: metricValue,
  })

  return color
}

export function getGapMetrics(report: ResearchReportDocument) {
  return [
    {
      title: 'Readability Grade',
      ideal_value: getReadabilityStatement(report.readability_ease.min).level,
      current_value: getReadabilityStatement(report.page_stats.reading_ease)
        .level,
      color: getColor(
        report.page_stats.reading_ease,
        report.readability_ease.min,
        report.readability_ease.max
      ),
    },
    {
      title: 'Content Length',
      ideal_value: renderMetricAsString(
        report.page_length.min,
        report.page_length.max
      ),
      current_value: `${Math.floor(report.page_stats.length)}`,
      color: getColor(
        report.page_stats.length,
        report.page_length.min,
        report.page_length.max
      ),
    },
    {
      title: 'Headings',
      ideal_value: renderMetricAsString(
        report.element_counts.hx.min,
        report.element_counts.hx.max
      ),
      current_value: `${Math.floor(report.page_stats.element_counts.hx)}`,
      color: getColor(
        Math.floor(report.page_stats.element_counts.hx),
        report.element_counts.hx.min,
        report.element_counts.hx.max
      ),
    },

    {
      title: 'Links',
      ideal_value: renderMetricAsString(
        report.element_counts.a.min,
        report.element_counts.a.max
      ),
      current_value: `${Math.floor(report.page_stats.element_counts.a)}`,
      info: 'Recommended number of links you should be using in the content',
      color: getColor(
        Math.floor(report.page_stats.element_counts.a),
        report.element_counts.a.min,
        report.element_counts.a.max
      ),
    },
  ]
}

function renderMetricAsString(min: number, max: number) {
  if (min === max) {
    return `${Math.floor(min)}`
  }
  return `${Math.floor(min)}-${Math.floor(max)}`
}

export function getSectionHeadingColor(colors: string[]) {
  const redState = 'var(--color-orange-700)'
  const greenState = 'var(--color-green-600)'

  const { redCount, greenCount } = colors.reduce(
    (counts, color) => {
      if (color === redState) {
        counts.redCount++
      } else if (color === greenState) {
        counts.greenCount++
      }
      return counts
    },
    { redCount: 0, greenCount: 0 }
  )

  if (redCount >= 3) {
    return 'RED'
  } else if (greenCount >= 3) {
    return 'GREEN'
  } else if (greenCount >= 2 || (greenCount && redCount === 1)) {
    return 'YELLOW'
  }

  return 'YELLOW'
}

export const getColorFromGrade = (grade: Grade) => {
  if (grade === 'A') return 'GREEN'
  else if (grade === 'B' || grade === 'C') return 'YELLOW'
  else if (grade === 'D' || grade === 'E') return 'RED'
}
